import clsx from "clsx";
import React from "react";

import sharedStyles from "../../styles/shared/inputs.module.css";
import styles from "./index.module.css";

export interface ButtonSwitchProps {
  /** First option label */
  optionOne: string;
  /** Second option label */
  optionTwo: string;
  /** Currently selected option */
  selectedOption?: string;
  /** Callback when selection changes */
  onChange?: (selectedOption: string) => void;
  /** Disabled state */
  isDisabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Accessibility label */
  "aria-label"?: string;
}

/**
 * A button switch component for toggling between two options
 */
const ButtonSwitch = React.forwardRef<HTMLButtonElement, ButtonSwitchProps>(
  (
    {
      optionOne,
      optionTwo,
      selectedOption,
      onChange,
      isDisabled = false,
      className,
      "aria-label": ariaLabel,
    },
    ref
  ) => {
    const currentSelection = selectedOption || optionOne;
    const isOptionTwoSelected = currentSelection === optionTwo;

    const handleClick = () => {
      if (isDisabled) return;
      const newSelection = isOptionTwoSelected ? optionOne : optionTwo;
      onChange?.(newSelection);
    };

    const classes = clsx(
      styles.root,
      {
        [sharedStyles.isDisabled]: isDisabled,
      },
      className
    );

    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={isOptionTwoSelected}
        aria-label={ariaLabel || `Switch between ${optionOne} and ${optionTwo}`}
        className={classes}
        disabled={isDisabled}
        onClick={handleClick}
      >
        <div className={styles.switchContainer}>
          <span
            className={clsx(styles.option, {
              [styles.optionActive]: currentSelection === optionOne,
            })}
          >
            {optionOne}
          </span>
          <span
            className={clsx(styles.option, {
              [styles.optionActive]: currentSelection === optionTwo,
            })}
          >
            {optionTwo}
          </span>
        </div>
      </button>
    );
  }
);

ButtonSwitch.displayName = "ButtonSwitch";
export default ButtonSwitch;
